/**
 * Carrousel d'avis.
 *
 * Le defilement automatique s'interrompt des que la visiteuse manifeste de
 * l'interet — survol, focus clavier, defilement manuel — et se coupe pour de
 * bon si elle appuie sur pause. Il ne demarre pas du tout quand le systeme
 * demande a limiter les animations, ni quand la section est hors de l'ecran.
 */
class ReviewsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-reviews-track]');
    this.pauseButton = this.querySelector('[data-reviews-pause]');
    if (!this.track) return;

    this.querySelector('[data-reviews-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-reviews-next]')?.addEventListener('click', () => this.step(1));

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.wanted = this.dataset.autoplay === 'true' && !this.reducedMotion;
    this.stopped = !this.wanted;
    this.interval = Number(this.dataset.interval) || 6000;

    this.pauseButton?.addEventListener('click', () => this.togglePause());
    if (this.stopped) this.reflectPause();

    // Une interaction suspend le defilement, sans le couper definitivement.
    ['pointerenter', 'focusin'].forEach((type) => {
      this.addEventListener(type, () => this.suspend(true));
    });
    ['pointerleave', 'focusout'].forEach((type) => {
      this.addEventListener(type, () => this.suspend(false));
    });
    this.track.addEventListener('scroll', () => this.onManualScroll(), { passive: true });

    // Rien ne defile tant que la section n'est pas a l'ecran.
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.sync();
      }, { threshold: 0.2 });
      this.observer.observe(this);
    } else {
      this.visible = true;
    }

    this.sync();
  }

  disconnectedCallback() {
    this.stopTimer();
    this.observer?.disconnect();
  }

  get slideWidth() {
    const first = this.track.querySelector('.reviews__card');
    if (!first) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || '0');
    return first.getBoundingClientRect().width + gap;
  }

  step(direction) {
    const width = this.slideWidth;
    const max = this.track.scrollWidth - this.track.clientWidth;
    let target = this.track.scrollLeft + direction * width;

    // Boucle : au-dela du dernier avis on revient au premier.
    if (target > max - 2) target = direction > 0 && this.track.scrollLeft >= max - 2 ? 0 : Math.min(target, max);
    if (target < 0) target = max;

    this.track.scrollTo({ left: target, behavior: this.reducedMotion ? 'auto' : 'smooth' });
  }

  onManualScroll() {
    // On laisse la main a la visiteuse un instant apres son geste.
    window.clearTimeout(this.scrollTimer);
    this.suspend(true);
    this.scrollTimer = window.setTimeout(() => this.suspend(false), 4000);
  }

  suspend(value) {
    this.suspended = value;
    this.sync();
  }

  togglePause() {
    this.stopped = !this.stopped;
    this.reflectPause();
    this.sync();
  }

  reflectPause() {
    this.dataset.paused = this.stopped ? 'true' : 'false';
    this.pauseButton?.setAttribute('aria-pressed', this.stopped ? 'true' : 'false');
  }

  sync() {
    const shouldRun = this.wanted && !this.stopped && !this.suspended && this.visible;
    if (shouldRun) this.startTimer();
    else this.stopTimer();
  }

  startTimer() {
    if (this.timer) return;
    this.timer = window.setInterval(() => this.step(1), this.interval);
  }

  stopTimer() {
    window.clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('reviews-carousel')) {
  customElements.define('reviews-carousel', ReviewsCarousel);
}
